import React, { useMemo } from 'react';
import { Pencil, Plus, Receipt, Trash2 } from 'lucide-react';
import { Badge, BadgeTone } from '../ui/arc/badge';
import { Button } from '../ui/arc/button';
import { DataGrid, GridCol } from '../ui/DataGrid';
import { EmptyState } from '../ui/arc/empty-state';
import { KpiCard, MonthPicker, Panel, monthLabel } from './costsUi';

// Pestaña Gastos de Costos & BI — presentación.
// El mes de cada gasto sale de su FECHA DEL GASTO (expense_date), igual que en Resultados; si
// no tiene, de created_at. Antes Gastos usaba created_at y un gasto imputado a otro mes caía en
// meses distintos según la pestaña.
// el alta/edición/borrado los sigue haciendo Costs.tsx (handlers que llegan por props).

export interface GastoRow {
    id: number; amount: number; provider_id: number | null; provider_name: string | null;
    payment_method: string; notes: string | null; created_at: string; created_by: string | null;
    category?: string | null; expense_date?: string | null;
}

export const PAYMENT_LABELS: Record<string, string> = {
    efectivo: 'Efectivo', transferencia: 'Transferencia', tarjeta: 'Tarjeta', cheque: 'Cheque', cuenta_corriente: 'Cuenta Corriente', financiera: 'Financiera',
};
const PAYMENT_TONE: Record<string, BadgeTone> = { financiera: 'neutral', efectivo: 'success', transferencia: 'info', tarjeta: 'warning', cheque: 'neutral', cuenta_corriente: 'neutral' };
export const EXPENSE_CATEGORIES: { value: string; label: string }[] = [
    { value: 'locacion', label: 'Locación' }, { value: 'sueldos', label: 'Sueldos' }, { value: 'marketing', label: 'Marketing' },
    { value: 'servicios_digitales', label: 'Servicios digitales' }, { value: 'insumos', label: 'Insumos / Mercadería' },
    { value: 'logistica', label: 'Logística / Envíos' }, { value: 'impuestos', label: 'Impuestos / Contador' }, { value: 'otros', label: 'Otros' },
    { value: 'sin_asignar', label: 'Sin asignar (financiera)' },
];
const CAT_LABEL: Record<string, string> = { ...Object.fromEntries(EXPENSE_CATEGORIES.map(c => [c.value, c.label])), mantenimiento: 'Mantenimiento' };

// Mismo formato que antes ($ + fmtARS con centavos).
const ars = (n: number) => '$' + n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const monthKey = (iso: string) => { const d = new Date(iso); return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`; };

/** Ranking con barra de magnitud (una sola serie → un solo tono; el % y el monto van en texto). */
function BreakdownList({ items, total }: { items: [string, number][]; total: number }) {
    const max = items.length ? items[0][1] : 0;
    return (
        <ul className="m-0 grid list-none gap-2.5 p-0">
            {items.map(([name, v]) => (
                <li key={name} className="grid gap-1">
                    <div className="flex items-baseline justify-between gap-3 text-[length:var(--text-sm)]">
                        <span className="min-w-0 truncate text-ink">{name}</span>
                        <span className="whitespace-nowrap tabular-nums text-ink">{ars(v)}
                            <span className="ml-2 inline-block w-12 text-right text-[length:var(--text-xs)] text-faint">{total > 0 ? ((v / total) * 100).toFixed(1) : 0}%</span>
                        </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-[var(--surface-muted)]">
                        <div className="h-full rounded-full bg-[color-mix(in_oklab,var(--text-secondary)_55%,var(--card))]" style={{ width: `${max > 0 ? (v / max) * 100 : 0}%` }} />
                    </div>
                </li>
            ))}
        </ul>
    );
}

// Fecha que manda para el mes del gasto (mediodía para que el huso no lo corra de día).
const gastoDate = (e: GastoRow) => e.expense_date ? e.expense_date + 'T12:00:00' : e.created_at;

export function GastosView({ expenses, month, setMonth, onNew, onEdit, onDelete }: {
    expenses: GastoRow[]; month: string; setMonth: (m: string) => void;
    onNew: () => void; onEdit: (e: GastoRow) => void; onDelete: (id: number) => void;
}) {
    const filtered = useMemo(() => month === 'all' ? expenses : expenses.filter(e => monthKey(gastoDate(e)) === month), [expenses, month]);

    const { total, byMethod, byProvider, byCategory } = useMemo(() => {
        const byMethod: Record<string, number> = {}, byProvider: Record<string, number> = {}, byCategory: Record<string, number> = {};
        filtered.forEach(e => {
            const m = e.payment_method || 'efectivo';
            byMethod[m] = (byMethod[m] || 0) + Number(e.amount);
            const p = e.provider_name || 'Sin proveedor';
            byProvider[p] = (byProvider[p] || 0) + Number(e.amount);
            const c = CAT_LABEL[e.category || 'otros'] || e.category || 'Otros';
            byCategory[c] = (byCategory[c] || 0) + Number(e.amount);
        });
        return { total: filtered.reduce((s, e) => s + Number(e.amount), 0), byMethod, byProvider, byCategory };
    }, [filtered]);

    // Desde el gasto más viejo hasta el mes actual (igual que antes).
    const months = useMemo(() => {
        const now = new Date();
        const currentKey = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
        const earliest = [...expenses.map(e => monthKey(gastoDate(e))), currentKey].sort()[0];
        const [startY, startM] = earliest.split('-').map(Number);
        const keys: string[] = [];
        let y = startY, m = startM;
        while (y < now.getFullYear() || (y === now.getFullYear() && m <= now.getMonth() + 1)) {
            keys.push(`${y}-${m.toString().padStart(2, '0')}`);
            m++; if (m > 12) { m = 1; y++; }
        }
        return keys.sort((a, b) => b.localeCompare(a));
    }, [expenses]);

    // Último mes con gastos, para el empty state ("¿Ir a junio?").
    const lastWithData = useMemo(() => expenses.length ? expenses.map(e => monthKey(gastoDate(e))).sort().reverse()[0] : null, [expenses]);

    const cols: GridCol<GastoRow>[] = [
        {
            key: 'fecha', header: 'Fecha', sort: e => gastoDate(e),
            render: e => {
                const loadedOther = monthKey(gastoDate(e)) !== monthKey(e.created_at);
                return (
                    <span className="grid">
                        <span className="tabular-nums">{new Date(gastoDate(e)).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                        {loadedOther && <span className="text-[length:var(--text-xs)] text-soft">cargado el {new Date(e.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })}</span>}
                    </span>
                );
            },
        },
        { key: 'provider_name', header: 'Proveedor', sort: e => e.provider_name || '', render: e => e.provider_name || <span className="text-faint">—</span> },
        { key: 'category', header: 'Categoría', hideOnMobile: true, sort: e => CAT_LABEL[e.category || 'otros'] || e.category || '', render: e => <span className="text-soft">{CAT_LABEL[e.category || 'otros'] || e.category}</span> },
        { key: 'payment_method', header: 'Forma de pago', hideOnMobile: true, sort: e => PAYMENT_LABELS[e.payment_method] || e.payment_method, render: e => <Badge size="sm" tone={PAYMENT_TONE[e.payment_method] || 'neutral'}>{PAYMENT_LABELS[e.payment_method] || e.payment_method}</Badge> },
        { key: 'notes', header: 'Observaciones', hideOnMobile: true, sort: e => e.notes || '', render: e => e.notes ? <span className="line-clamp-2 text-soft" title={e.notes}>{e.notes}</span> : <span className="text-faint">—</span> },
        { key: 'amount', header: 'Monto', align: 'right', sort: e => Number(e.amount), render: e => <span className="font-semibold tabular-nums">{ars(Number(e.amount))}</span> },
        {
            key: 'actions', header: '', align: 'right', width: 'w-24',
            render: e => (
                <span className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" aria-label={`Editar gasto ${e.provider_name || ''} ${ars(Number(e.amount))}`} title="Editar" onClick={() => onEdit(e)}><Pencil size={15} /></Button>
                    <Button variant="ghost" size="sm" aria-label={`Borrar gasto ${e.provider_name || ''} ${ars(Number(e.amount))}`} title="Eliminar" onClick={() => onDelete(e.id)}><Trash2 size={15} className="text-[var(--danger)]" /></Button>
                </span>
            ),
        },
    ];

    const methods = Object.entries(byMethod).sort((a, b) => b[1] - a[1]);
    const providers = Object.entries(byProvider).sort((a, b) => b[1] - a[1]);
    const categories = Object.entries(byCategory).sort((a, b) => b[1] - a[1]);

    return (
        <div className="tw w-full min-w-0 pb-12 pt-1">
            <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
                <div className="min-w-0">
                    <h1 className="m-0 text-[length:var(--text-2xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">Gastos</h1>
                    <p className="m-0 mt-1 max-w-[72ch] text-[length:var(--text-sm)] text-soft">
                        Pagos que no son costos fijos ni mercadería de un pedido. Hoy no restan en Resultados: se muestran aparte.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <MonthPicker months={months} value={month} onChange={setMonth} extra={[{ value: 'all', label: 'Todos los meses' }]} />
                    <Button onClick={onNew}><Plus size={16} /> Nuevo gasto</Button>
                </div>
            </header>

            {filtered.length === 0 ? (
                <div className="rounded-[var(--radius-surface)] border border-dashed border-line bg-card py-6">
                    <EmptyGastos month={month} lastWithData={lastWithData} onGo={setMonth} onNew={onNew} />
                </div>
            ) : (<>
                <section className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <KpiCard label={month === 'all' ? 'Total histórico' : 'Total del mes'} value={ars(total)}
                        context={`${filtered.length} gasto${filtered.length === 1 ? '' : 's'}`} />
                    {methods.slice(0, 3).map(([m, v]) => (
                        <KpiCard key={m} label={PAYMENT_LABELS[m] || m} value={ars(v)}
                            context={`${total > 0 ? ((v / total) * 100).toFixed(0) : 0}% del total`} />
                    ))}
                </section>
                {methods.length > 3 && (
                    <p className="-mt-3 mb-5 text-[length:var(--text-sm)] text-soft">
                        {methods.slice(3).map(([m, v]) => `${PAYMENT_LABELS[m] || m}: ${ars(v)}`).join(' · ')}
                    </p>
                )}

                <DataGrid rows={filtered} cols={cols} pageSize={50} rowKey={e => e.id}
                    searchPlaceholder="Buscar proveedor u observación…"
                    title={<span className="text-[length:var(--text-sm)] text-soft">{month === 'all' ? 'Todos los meses' : monthLabel(month)}</span>} />

                <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {providers.length > 1 && (
                        <Panel title="Por proveedor" subtitle={`${providers.length} proveedores`}>
                            <BreakdownList items={providers} total={total} />
                        </Panel>
                    )}
                    <Panel title="Por categoría" subtitle="la que se usa en Resultados">
                        <BreakdownList items={categories} total={total} />
                    </Panel>
                </div>
            </>)}
        </div>
    );
}

function EmptyGastos({ month, lastWithData, onGo, onNew }: { month: string; lastWithData: string | null; onGo: (m: string) => void; onNew: () => void }) {
    const canJump = month !== 'all' && lastWithData && lastWithData !== month;
    return (
        <EmptyState icon={<Receipt size={20} />}
            title={month === 'all' ? 'Todavía no hay gastos' : `Sin gastos en ${monthLabel(month).toLowerCase()}`}
            description={canJump ? `El último gasto cargado es de ${monthLabel(lastWithData!).toLowerCase()}.` : 'Cargá el primero con el botón de abajo.'}
            action={<div className="flex flex-wrap justify-center gap-2">
                {canJump && <Button variant="secondary" onClick={() => onGo(lastWithData!)}>Ver {monthLabel(lastWithData!).toLowerCase()}</Button>}
                <Button onClick={onNew}><Plus size={16} /> Nuevo gasto</Button>
            </div>} />
    );
}
